import React from 'react';
import { toast } from 'react-toastify';

const WeightConfigurator = ({ recipients, weights, onChange }) => {
  const [longPressTimer, setLongPressTimer] = React.useState(null);
  const [longPressInterval, setLongPressInterval] = React.useState(null);
  const lastEventTypeRef = React.useRef(null);
  const lastEventTimeRef = React.useRef(0);
  const touchMovedRef = React.useRef(false);
  const touchStartPositionRef = React.useRef({ x: 0, y: 0 });

  // Calculate totals
  const validRecipientIds = new Set(recipients.map(r => r.id));
  const validWeights = Object.entries(weights).filter(([id]) => validRecipientIds.has(id));
  const totalWeight = validWeights.reduce((sum, [_, w]) => sum + (parseInt(w) || 0), 0);
  const activeRecipientCount = validWeights.filter(([_, w]) => (parseInt(w) || 0) > 0).length;

  // Cleanup timers on unmount
  React.useEffect(() => {
    return () => {
      if (longPressTimer) clearTimeout(longPressTimer);
      if (longPressInterval) clearInterval(longPressInterval);
    };
  }, [longPressTimer, longPressInterval]);

  const handleWeightIncrement = (recipientId, increment) => {
    const currentWeight = parseInt(weights[recipientId]) || 0;
    const newWeight = currentWeight + increment;
    if (newWeight >= 0 && newWeight <= 100) {
      onChange({
        ...weights,
        [recipientId]: newWeight
      });
    }
  };

  const handleWeightChange = (recipientId, value) => {
    const newWeight = parseInt(value) || 0;
    if (newWeight >= 0 && newWeight <= 100) {
      onChange({
        ...weights,
        [recipientId]: newWeight
      });
    }
  };

  const handleDistributeEqually = () => {
    if (recipients.length === 0) return;

    const equalWeight = 1;
    const updatedWeights = {};

    recipients.forEach(recipient => {
      updatedWeights[recipient.id] = equalWeight;
    });

    onChange(updatedWeights);
    toast.success(`Set equal weight of ${equalWeight} for all ${recipients.length} recipients`);
  };

  const handleClearWeights = () => {
    const clearedWeights = {};

    recipients.forEach(recipient => {
      clearedWeights[recipient.id] = 0;
    });

    onChange(clearedWeights);
    toast.info('Cleared all weights');
  };

  // Long press handlers for faster incrementing/decrementing
  const handlePressStart = (recipientId, increment, e) => {
    const eventType = e?.type || '';
    const now = Date.now();

    // Prevent duplicate events: if a touch event just fired, ignore subsequent mouse event
    if (eventType === 'mousedown' && lastEventTypeRef.current === 'touchstart' && (now - lastEventTimeRef.current) < 500) {
      return; // Skip this mouse event as it's a duplicate of the touch event
    }

    // Track this event
    lastEventTypeRef.current = eventType;
    lastEventTimeRef.current = now;

    // For touch events, track initial position and reset moved flag
    if (eventType === 'touchstart') {
      const touch = e.touches[0];
      touchStartPositionRef.current = { x: touch.clientX, y: touch.clientY };
      touchMovedRef.current = false;
    }

    // First increment on press (only for mouse, touch will wait for touchend)
    if (eventType === 'mousedown') {
      handleWeightIncrement(recipientId, increment);
    }

    // Start long-press after 500ms
    const timer = setTimeout(() => {
      // Only start long press if touch hasn't moved
      if (eventType === 'touchstart' && touchMovedRef.current) {
        return;
      }
      const interval = setInterval(() => {
        handleWeightIncrement(recipientId, increment);
      }, 100); // Repeat every 100ms
      setLongPressInterval(interval);
    }, 500);

    setLongPressTimer(timer);
  };

  const handleTouchMove = (e) => {
    // Check if finger moved significantly (more than 10px)
    const touch = e.touches[0];
    const deltaX = Math.abs(touch.clientX - touchStartPositionRef.current.x);
    const deltaY = Math.abs(touch.clientY - touchStartPositionRef.current.y);

    if (deltaX > 10 || deltaY > 10) {
      touchMovedRef.current = true;
      // Cancel timers if user is scrolling
      if (longPressTimer) {
        clearTimeout(longPressTimer);
        setLongPressTimer(null);
      }
      if (longPressInterval) {
        clearInterval(longPressInterval);
        setLongPressInterval(null);
      }
    }
  };

  const handlePressEnd = (recipientId, increment, e) => {
    const eventType = e?.type || '';
    const now = Date.now();

    // Prevent duplicate events
    if (eventType === 'mouseup' && lastEventTypeRef.current === 'touchstart' && (now - lastEventTimeRef.current) < 500) {
      return; // Skip this mouse event
    }

    // For touch end, only increment if there was no movement (true tap, not scroll)
    if (eventType === 'touchend' && !touchMovedRef.current) {
      handleWeightIncrement(recipientId, increment);
    }

    // Reset touch moved flag
    touchMovedRef.current = false;

    if (longPressTimer) {
      clearTimeout(longPressTimer);
      setLongPressTimer(null);
    }
    if (longPressInterval) {
      clearInterval(longPressInterval);
      setLongPressInterval(null);
    }
  };

  return (
    <div className="bg-white rounded-lg p-3 md:p-4 lg:p-5 shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3">
        <div>
          <h3 className="m-0 mb-1 text-lg text-gray-800">Recipient Weights</h3>
          <p className="m-0 text-gray-500 text-xs">
            Set distribution weights for each recipient (0-100)
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="px-4 py-2 bg-tenq-gold text-white border-0 rounded-md text-xs font-semibold cursor-pointer transition-all hover:bg-tenq-gold-dark active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleDistributeEqually}
            disabled={recipients.length === 0}
            title="Set equal weight for all recipients"
          >
            Distribute Equally
          </button>
          <button
            className="px-4 py-2 bg-gray-100 text-gray-700 border border-gray-200 rounded-md text-xs font-medium cursor-pointer transition-all hover:bg-gray-200 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={handleClearWeights}
            disabled={recipients.length === 0 || totalWeight === 0}
            title="Set all weights to 0"
          >
            Clear All
          </button>
        </div>
      </div>

      <div className="flex flex-col gap-2.5 mb-5">
        {recipients.map(recipient => {
          const recipientWeight = parseInt(weights[recipient.id]) || 0;
          const weightPercentage = totalWeight > 0 ? ((recipientWeight / totalWeight) * 100) : 0;

          return (
            <div key={recipient.id} className="flex flex-col gap-1.5 p-2 md:p-3 bg-gray-50 rounded-md border border-gray-100">
              <div className="flex flex-row justify-between items-center gap-2">
                <div className="flex-1 min-w-0 flex flex-col gap-0.5">
                  <div className="flex items-center gap-1.5 flex-wrap">
                    <span className="font-semibold text-gray-800 text-sm md:text-base truncate">
                      {recipient.name}
                    </span>
                    {totalWeight > 0 && recipientWeight > 0 && (
                      <span className="text-xs font-semibold text-tenq-gold bg-tenq-gold/10 px-1.5 py-0.5 rounded whitespace-nowrap">
                        {weightPercentage.toFixed(0)}%
                      </span>
                    )}
                  </div>
                  <span className="text-xs text-gray-500">
                    {recipient.role} • {recipient.email}
                  </span>
                </div>
                <div className="flex items-center gap-0 bg-white border border-gray-200 rounded-md overflow-hidden shadow-sm flex-shrink-0">
                  <button
                    className="w-8 md:w-9 h-8 md:h-9 flex items-center justify-center bg-gray-50 border-0 text-gray-800 text-lg md:text-xl font-semibold cursor-pointer transition-all select-none hover:bg-tenq-gold hover:text-white active:bg-tenq-gold-dark active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-800 touch-manipulation"
                    onMouseDown={(e) => handlePressStart(recipient.id, -1, e)}
                    onMouseUp={(e) => handlePressEnd(recipient.id, -1, e)}
                    onMouseLeave={(e) => handlePressEnd(recipient.id, -1, e)}
                    onTouchStart={(e) => handlePressStart(recipient.id, -1, e)}
                    onTouchMove={(e) => handleTouchMove(e)}
                    onTouchEnd={(e) => handlePressEnd(recipient.id, -1, e)}
                    disabled={recipientWeight <= 0}
                  >
                    −
                  </button>
                  <input
                    type="number"
                    min="0"
                    max="100"
                    value={recipientWeight}
                    onChange={(e) => handleWeightChange(recipient.id, e.target.value)}
                    className="w-12 md:w-14 py-1.5 md:py-2 px-1 border-0 border-l border-r border-gray-200 text-sm md:text-sm font-semibold text-center text-gray-800 bg-white focus:outline-none focus:bg-tenq-gold/5 focus:ring-2 focus:ring-tenq-gold focus:ring-inset [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none touch-manipulation"
                    inputMode="numeric"
                  />
                  <button
                    className="w-8 md:w-9 h-8 md:h-9 flex items-center justify-center bg-gray-50 border-0 text-gray-800 text-lg md:text-xl font-semibold cursor-pointer transition-all select-none hover:bg-tenq-gold hover:text-white active:bg-tenq-gold-dark active:scale-95 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-gray-50 disabled:hover:text-gray-800 touch-manipulation"
                    onMouseDown={(e) => handlePressStart(recipient.id, 1, e)}
                    onMouseUp={(e) => handlePressEnd(recipient.id, 1, e)}
                    onMouseLeave={(e) => handlePressEnd(recipient.id, 1, e)}
                    onTouchStart={(e) => handlePressStart(recipient.id, 1, e)}
                    onTouchMove={(e) => handleTouchMove(e)}
                    onTouchEnd={(e) => handlePressEnd(recipient.id, 1, e)}
                    disabled={recipientWeight >= 100}
                  >
                    +
                  </button>
                </div>
              </div>

              {/* Visual Weight Progress Bar */}
              {totalWeight > 0 && recipientWeight > 0 && (
                <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                  <div
                    className="bg-gradient-to-r from-tenq-gold to-tenq-gold-dark h-1.5 rounded-full transition-all duration-300 ease-out"
                    style={{ width: `${weightPercentage}%` }}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div className="flex flex-col md:flex-row gap-8 md:gap-8 p-4 bg-gray-100 rounded-md">
        <div className="flex gap-2 items-center text-sm">
          <span>Active Recipients:</span>
          <strong className="text-tenq-gold text-lg">{activeRecipientCount}</strong>
        </div>
        <div className="flex gap-2 items-center text-sm">
          <span>Total Weight:</span>
          <strong className="text-tenq-gold text-lg">{totalWeight}</strong>
        </div>
      </div>

      {/* Weight Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mt-4">
        <p className="text-sm text-blue-800">
          <strong>How weights work:</strong> If you set weights [A:30, B:20, C:10], the system creates a queue maintaining exact proportions. Weight range: 0-100. Long-press +/- buttons for faster adjustments.
        </p>
      </div>
    </div>
  );
};

export default WeightConfigurator;
